/**
 * Hook para mostrar versión en UI (sin aplicar autorefresh).
 * @module hooks/useAppVersionInfo
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchAppVersion,
  getLocalAppVersionSnapshot,
  getPendingAppVersion,
  resolveAppVersionStatus,
  getVersionCheckIntervalMs
} from '../services/appVersion.service';
import { PENDING_APP_VERSION_KEY, APP_VERSION_SNAPSHOT_KEY } from '../utils/versionStoragePolicy';

const defaultState = {
  loading: true,
  remote: null,
  local: null,
  pending: null,
  status: 'unknown',
  error: null
};

export function useAppVersionInfo({ poll = true } = {}) {
  const [state, setState] = useState(defaultState);

  const refresh = useCallback(async () => {
    try {
      const remote = await fetchAppVersion();
      if (!remote) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: new Error('version.json no disponible'),
          status: 'unknown'
        }));
        return;
      }
      const local = getLocalAppVersionSnapshot();
      const pending = getPendingAppVersion();
      const status = resolveAppVersionStatus(local, remote, pending);
      setState({
        loading: false,
        remote,
        local,
        pending,
        status,
        error: null
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error,
        status: 'unknown'
      }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await refresh();
    };
    run();

    if (!poll) {
      return () => {
        cancelled = true;
      };
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);

    const onStorage = (event) => {
      if (
        event.key === APP_VERSION_SNAPSHOT_KEY
        || event.key === PENDING_APP_VERSION_KEY
        || event.key === null
      ) {
        run();
      }
    };
    window.addEventListener('storage', onStorage);

    const intervalId = window.setInterval(run, getVersionCheckIntervalMs());

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storage', onStorage);
      window.clearInterval(intervalId);
    };
  }, [poll, refresh]);

  return { ...state, refresh };
}

export default useAppVersionInfo;
